import { Form, Input, Button, Card, message, Space, Typography } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { AxiosError } from 'axios';
import { apiClient } from '../../api/axios';
import { useAuthStore } from '../../store/authStore';

interface LoginForm {
  email: string;
  password: string;
}

// Only embed test credentials when the build explicitly opts in (never in production defaults)
const testAccounts: LoginForm[] =
  import.meta.env.VITE_SHOW_TEST_CREDENTIALS === 'true'
    ? [
        { email: 'admin@hepaderm.ro', password: 'Admin123!' },
        { email: 'dermatolog@hepaderm.ro', password: 'Dermato123!' },
        { email: 'hepatolog@hepaderm.ro', password: 'Hepato123!' },
      ]
    : [];

const showTestAccounts = testAccounts.length > 0;

const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm<LoginForm>();
  const navigate = useNavigate();
  const { login } = useAuthStore();

  const onFinish = async (values: LoginForm) => {
    setLoading(true);
    try {
      const response = await apiClient.post('/auth/login', {
        ...values,
        email: values.email.trim().toLowerCase(),
      });
      console.log('[Login] response:', response.status, response.data);
      const payload = response.data.data ?? response.data;
      const { user, accessToken } = payload;

      console.log('[Login] calling store.login with:', { user, hasToken: !!accessToken });
      login(user, accessToken);
      message.success('Autentificare reușită');
      console.log('[Login] navigating to /dashboard');
      navigate('/dashboard');
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const fallback =
        axiosError.response?.status === 401
          ? 'Email sau parolă incorectă. Verifică dacă folosești contul de test corect.'
          : 'Autentificarea nu a reușit. Încearcă din nou.';

      message.error(axiosError.response?.data?.message || fallback);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: 16,
    }}>
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
            <img src="/hepadskin-logo.svg" alt="HEPASKIN" style={{ width: 36, height: 36 }} />
            <span style={{ fontSize: 24, fontWeight: 'bold' }}>HEPASKIN</span>
          </div>
        }
        style={{ width: '100%', maxWidth: 400, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
      >
        <Typography.Paragraph
          type="secondary"
          style={{ textAlign: 'center', marginTop: -8, marginBottom: 20 }}
        >
          Registrul transfrontalier pentru cancer cutanat și hepatic
        </Typography.Paragraph>
        <Form
          form={form}
          name="login"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
        >
          <Form.Item
            name="email"
            normalize={(value: string) => value?.trim()}
            rules={[
              { required: true, message: 'Vă rugăm introduceți email-ul' },
              { type: 'email', message: 'Email invalid' },
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Email"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: 'Vă rugăm introduceți parola' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Parolă"
              size="large"
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block size="large">
              Autentificare
            </Button>
          </Form.Item>
        </Form>

        {showTestAccounts && (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Typography.Text type="secondary">Conturi test (click = completare + autentificare automată)</Typography.Text>
            {testAccounts.map((account) => (
              <Button
                key={account.email}
                block
                onClick={async () => {
                  form.setFieldsValue(account);
                  console.log('[DemoLogin] Submitting for account:', account);
                  try {
                    await onFinish(account);
                  } catch (err) {
                    console.error('[DemoLogin] onFinish threw:', err);
                  }
                }}
              >
                {account.email}
              </Button>
            ))}
          </Space>
        )}
      </Card>
    </div>
  );
};

export default LoginPage;
